import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import api from '../api';

export default function Dashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/dashboard').then(r => setStats(r.data)).finally(() => setLoading(false));
  }, []);

  const fmt = n => `PKR ${(n || 0).toLocaleString()}`;

  const cards = stats ? [
    { icon: '🎓', label: 'Total Students', value: stats.totalStudents, tone: 'blue' },
    { icon: '🏠', label: 'Occupied Rooms', value: `${stats.occupiedRooms} / ${stats.totalRooms}`, tone: 'purple' },
    { icon: '✅', label: 'Available Rooms', value: stats.availableRooms, tone: 'green' },
    { icon: '💰', label: 'Monthly Income', value: fmt(stats.monthlyIncome), tone: 'cyan' },
    { icon: '💸', label: 'Monthly Expenses', value: fmt(stats.monthlyExpenses), tone: 'coral' },
    { icon: '⚠️', label: 'Pending Fees', value: fmt(stats.pendingFees), tone: 'amber' },
    { icon: '🛠️', label: 'Open User Reports', value: stats.openUserReports || 0, tone: 'pink' },
  ] : [];

  return (
    <Layout title="Dashboard">
      {loading ? (
        <div className="text-center" style={{ padding: 60, color: '#718096' }}>Loading...</div>
      ) : (
        <>
          <section className="dashboard-hero">
            <div><span>📅 {stats?.currentMonth} overview</span><h2>Your hostel, clearly organized.</h2><p>Students, rooms, payments and support requests are all one click away.</p></div>
            <div className="dashboard-hero-badge"><strong>{stats?.freeBeds || 0}</strong><span>Free beds today</span></div>
          </section>

          <div className={`dashboard-attention ${stats?.openUserReports ? 'needs-attention' : 'all-clear'}`}><div className="dashboard-attention-icon">{stats?.openUserReports ? '🔔' : '✨'}</div><div><h3>{stats?.openUserReports ? `${stats.openUserReports} problem report${stats.openUserReports === 1 ? '' : 's'} need attention` : 'Everything looks clear'}</h3><p>Review, reply and mark resident problems resolved from one inbox.</p></div><button className="btn btn-primary" onClick={() => navigate('/admin/reports')}>Open problem inbox</button></div>

          <div className="dashboard-quick-actions">
            <button onClick={() => navigate('/admin/students/add')}><span>➕</span><strong>Add student</strong><small>Create profile &amp; login</small></button>
            <button onClick={() => navigate('/admin/fees')}><span>💳</span><strong>Manage fees</strong><small>Payments &amp; reminders</small></button>
            <button onClick={() => navigate('/admin/rooms')}><span>🛏️</span><strong>Rooms &amp; floors</strong><small>Beds and occupancy</small></button>
            <button onClick={() => navigate('/admin/user-accounts')}><span>🔐</span><strong>User accounts</strong><small>Login access</small></button>
          </div>

          <div className="stats-grid">
            {cards.map((c, i) => (
              <div key={i} className={`stat-card stat-card-${c.tone}`}>
                <div className="stat-icon">
                  {c.icon}
                </div>
                <div className="stat-info">
                  <h3 style={{ fontSize: typeof c.value === 'string' && c.value.startsWith('PKR') ? 15 : 24 }}>
                    {c.value}
                  </h3>
                  <p>{c.label}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="dashboard-panels">
            <div className="card dashboard-summary-card dashboard-summary-finance">
              <div className="card-header">
                <h3 className="card-title">📊 Financial Summary</h3>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <Row label="Fee Income" value={fmt(stats?.monthlyIncome)} color="#276749" />
                <Row label="Total Expenses" value={fmt(stats?.monthlyExpenses)} color="#c53030" />
                <Row
                  label="Net Balance"
                  value={fmt((stats?.monthlyIncome || 0) - (stats?.monthlyExpenses || 0))}
                  color={(stats?.monthlyIncome || 0) >= (stats?.monthlyExpenses || 0) ? '#276749' : '#c53030'}
                />
                <Row label="Pending Fees" value={fmt(stats?.pendingFees)} color="#d69e2e" />
              </div>
            </div>

            <div className="card dashboard-summary-card dashboard-summary-rooms">
              <div className="card-header">
                <h3 className="card-title">🏠 Room Status</h3>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <Row label="Total Rooms" value={stats?.totalRooms} />
                <Row label="Total Beds" value={stats?.totalBeds} />
                <Row label="Free Beds" value={stats?.freeBeds} color="#276749" />
                <Row label="Occupied" value={stats?.occupiedRooms} color="#c53030" />
                <Row label="Available" value={stats?.availableRooms} color="#276749" />
                <div style={{ marginTop: 8 }}>
                  <div className="dashboard-progress">
                    <div
                      style={{
                        width: `${((stats?.occupiedRooms || 0) / (stats?.totalRooms || 1)) * 100}%`,
                      }}
                    />
                  </div>
                  <p style={{ fontSize: 11, color: '#718096', marginTop: 4 }}>
                    {Math.round(((stats?.occupiedRooms || 0) / (stats?.totalRooms || 1)) * 100)}% occupied
                  </p>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </Layout>
  );
}

function Row({ label, value, color }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #f0f4f8' }}>
      <span style={{ fontSize: 13, color: '#718096' }}>{label}</span>
      <span style={{ fontSize: 13, fontWeight: 600, color: color || '#2d3748' }}>{value}</span>
    </div>
  );
}
